import React, { createContext, useContext } from "react";
import type { BaseEntity } from "../../UniversalCRMTable/types";
import type { Callbacks } from "./types";

/**
 * UniversalEntityPageCallbacksContext
 *
 * Contains all memoized callbacks and render functions:
 * - handleGroupingExport: Memoized grouping export handler
 * - renderSimpleTable: Memoized table renderer
 *
 * Separated from other contexts to prevent unnecessary re-renders
 * when data, handlers, or UI state changes.
 */
export type UniversalEntityPageCallbacksContextValue<T extends BaseEntity> = Callbacks<T>;

// Entity type erased at the storage boundary; see UniversalEntityPageUIContext.
const UniversalEntityPageCallbacksContext = createContext<
  UniversalEntityPageCallbacksContextValue<BaseEntity> | undefined
>(undefined);

export function UniversalEntityPageCallbacksProvider<T extends BaseEntity>({
  children,
  value,
}: {
  children: React.ReactNode;
  value: UniversalEntityPageCallbacksContextValue<T>;
}) {
  return (
    <UniversalEntityPageCallbacksContext.Provider
      value={value as unknown as UniversalEntityPageCallbacksContextValue<BaseEntity>}
    >
      {children}
    </UniversalEntityPageCallbacksContext.Provider>
  );
}

/** Non-throwing read — `null` outside a provider. */
export function useOptionalUniversalEntityPageCallbacksContext<
  T extends BaseEntity = BaseEntity,
>(): UniversalEntityPageCallbacksContextValue<T> | null {
  const context = useContext(UniversalEntityPageCallbacksContext);
  return (context as unknown as UniversalEntityPageCallbacksContextValue<T> | undefined) ?? null;
}

export function useUniversalEntityPageCallbacksContext<
  T extends BaseEntity = BaseEntity,
>(): UniversalEntityPageCallbacksContextValue<T> {
  const context = useOptionalUniversalEntityPageCallbacksContext<T>();
  if (!context) {
    throw new Error(
      "useUniversalEntityPageCallbacksContext must be used within UniversalEntityPageCallbacksProvider"
    );
  }
  return context;
}
